import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, STATUSES, statusMeta } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Sparkles, Mail, Phone, MapPin, Calendar, DollarSign, Edit, Trash2, MessageSquare, Zap, Copy, Clock, CheckCircle2, XCircle, ShieldOff, Radar, FileText, Briefcase, Link2, Tag, Globe, KeyRound, Fingerprint } from "lucide-react";
import LeadFormDialog from "@/components/LeadFormDialog";
import SmsThread from "@/components/SmsThread";
import StreetViewPanel from "@/components/StreetViewPanel";
import { toast } from "sonner";

const LOST_REASONS = [
  { key: "price", label: "Price" },
  { key: "no_response", label: "No Response" },
  { key: "competitor", label: "Competitor" },
  { key: "timing", label: "Timing" },
  { key: "other", label: "Other" },
];

export default function LeadProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [automations, setAutomations] = useState({ tasks: [], next_scheduled: null, counts: { scheduled: 0, completed: 0, canceled: 0 }, sales_active: true, automation_active: false });
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const [scoring, setScoring] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(null); // {channel, draft}
  const [action, setAction] = useState(null);
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState("price");
  const [recommendation, setRecommendation] = useState(null);
  const [recLoading, setRecLoading] = useState(false);

  const load = async () => {
    try {
      const [l, a, c, au] = await Promise.all([
        api.get(`/leads/${id}`),
        api.get(`/leads/${id}/activities`),
        api.get(`/campaigns`),
        api.get(`/leads/${id}/automations`),
      ]);
      setLead(l.data);
      setActivities(a.data);
      setCampaigns(c.data);
      setAutomations(au.data);
    } catch {
      toast.error("Lead not found");
      nav("/app/leads");
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (status) => {
    if (status === "lost") {
      setLostOpen(true);
      return;
    }
    setLead((l) => ({ ...l, status }));
    try {
      await api.put(`/leads/${id}`, { status });
      toast.success("Status updated");
      if (status === "follow_up") {
        // Auto-fetch follow-up recommendation
        fetchRecommendation();
      }
      load();
    } catch { toast.error("Failed"); }
  };

  const confirmLost = async () => {
    setLostOpen(false);
    setLead((l) => ({ ...l, status: "lost", lost_reason: lostReason }));
    try {
      await api.put(`/leads/${id}`, { status: "lost", lost_reason: lostReason });
      toast.success("Marked as lost");
      load();
    } catch { toast.error("Failed"); }
  };

  const fetchRecommendation = async () => {
    setRecLoading(true);
    try {
      const { data } = await api.post("/ai/follow-up-recommendation", { lead_id: id });
      setRecommendation(data);
      setAction(data.next_action);
      toast.success("AI recommendation ready");
    } catch { toast.error("AI recommendation failed"); }
    finally { setRecLoading(false); }
  };

  const cancelTask = async (taskId) => {
    try {
      await api.post(`/leads/${id}/automations/${taskId}/cancel`);
      toast.success("Automation canceled");
      load();
    } catch { toast.error("Failed"); }
  };

  const completeTask = async (taskId) => {
    try {
      await api.post(`/leads/${id}/automations/${taskId}/complete`);
      toast.success("Marked as done");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      load();
    }
  };

  const dispatchNow = async () => {
    try {
      const { data } = await api.post("/automations/dispatch-now");
      const sent = data.results.filter((r) => r.result === "sent").length;
      toast.success(`Dispatched ${sent} of ${data.processed} task(s)`);
      load();
    } catch { toast.error("Dispatch failed"); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/leads/${id}/activities`, { type: "note", content: note });
      setNote("");
      load();
    } catch { toast.error("Failed"); }
  };

  const scoreLead = async () => {
    setScoring(true);
    try {
      const { data } = await api.post("/ai/score-lead", { lead_id: id });
      setAction(data.recommended_action);
      toast.success(`Lead scored: ${data.score}/100`);
      load();
    } catch { toast.error("AI scoring failed"); }
    finally { setScoring(false); }
  };

  const draftMessage = async (channel) => {
    setDrafting(true);
    setDraft(null);
    try {
      const { data } = await api.post("/ai/draft-follow-up", { lead_id: id, channel });
      setDraft(data);
    } catch { toast.error("AI draft failed"); }
    finally { setDrafting(false); }
  };

  const saveEdit = async (payload) => {
    try {
      await api.put(`/leads/${id}`, payload);
      toast.success("Lead updated");
      setEditing(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const deleteLead = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); nav("/app/leads"); } catch { toast.error("Failed"); }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — highlight and copy manually.");
    }
  };

  if (!lead) return <div className="p-8 text-zinc-500">Loading…</div>;
  const s = statusMeta(lead.status);

  return (
    <div className="p-8 max-w-7xl">
      <button onClick={()=>nav("/app/leads")} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="lead-back-btn">
        <ArrowLeft className="w-4 h-4"/> All leads
      </button>

      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Lead profile</div>
          <h1 className="font-heading text-4xl font-bold">{lead.customer_name}</h1>
          <div className="mt-2 text-zinc-600 text-sm">{lead.service_requested || "No service specified"}</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={lead.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-44" data-testid="lead-status-dropdown"><SelectValue/></SelectTrigger>
            <SelectContent>
              {STATUSES.map((st) => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={()=>nav(`/app/jobs?lead_id=${lead.id}`)} data-testid="lead-create-job-btn"><Briefcase className="w-4 h-4 mr-1"/> Job</Button>
          <Button variant="outline" onClick={()=>nav(`/app/estimates/new?lead_id=${lead.id}`)} data-testid="lead-create-estimate-btn"><FileText className="w-4 h-4 mr-1"/> Estimate</Button>
          <Button variant="outline" onClick={()=>setEditing(true)} data-testid="lead-edit-btn"><Edit className="w-4 h-4 mr-1"/> Edit</Button>
          <Button variant="outline" onClick={deleteLead} className="text-red-600 hover:text-red-700" data-testid="lead-delete-btn"><Trash2 className="w-4 h-4"/></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT — 8 cols */}
        <div className="lg:col-span-8 space-y-6">
          {/* AI card */}
          <div className="relative rounded-xl border border-orange-200 bg-orange-50/60 p-6 ai-glow">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#FF5A00] flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white"/>
                </div>
                <div>
                  <div className="text-xs font-mono-accent uppercase tracking-widest text-[#E04F00]">Gemini AI · Lead intelligence</div>
                  <div className="font-heading text-xl font-bold">Recommended next action</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={scoreLead} disabled={scoring} className="bg-white text-[#FF5A00] border border-orange-300 hover:bg-orange-100 btn-lift ai-pulse" data-testid="ai-score-lead-btn">
                  <Zap className="w-4 h-4 mr-1"/> {scoring ? "Scoring…" : (lead.ai_score != null ? "Rescore" : "Score lead")}
                </Button>
                <Button onClick={()=>draftMessage("email")} disabled={drafting} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="draft-ai-email-button">
                  <Mail className="w-4 h-4 mr-1"/> Draft email
                </Button>
                <Button onClick={()=>draftMessage("sms")} disabled={drafting} variant="outline" data-testid="draft-ai-sms-button">
                  <MessageSquare className="w-4 h-4 mr-1"/> Draft SMS
                </Button>
              </div>
            </div>

            <div className="mt-5 grid md:grid-cols-3 gap-4">
              <div className="bg-white border border-orange-200 rounded-lg p-4">
                <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">AI Score</div>
                <div className="font-mono-accent text-4xl font-bold text-[#FF5A00]" data-testid="lead-ai-score">{lead.ai_score ?? "—"}</div>
                {lead.ai_score_reason && <div className="text-xs text-zinc-600 mt-2">{lead.ai_score_reason}</div>}
              </div>
              <div className="md:col-span-2 bg-white border border-orange-200 rounded-lg p-4">
                <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">Suggested action</div>
                <div className="text-zinc-800 font-medium">{action || (lead.ai_score != null ? "Run 'Score lead' again for an updated recommendation." : "Click 'Score lead' to generate an AI recommendation.")}</div>
              </div>
            </div>
          </div>

          {/* Follow-up AI recommendation (shows on follow_up stage) */}
          {lead.status === "follow_up" && (
            <div className="bg-white border border-orange-200 rounded-xl p-6 card-crisp" data-testid="follow-up-recommendation-card">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FF5A00] flex items-center justify-center">
                    <Radar className="w-5 h-5 text-white"/>
                  </div>
                  <div>
                    <div className="text-xs font-mono-accent uppercase tracking-widest text-[#E04F00]">Follow-up intelligence</div>
                    <h2 className="font-heading text-xl font-bold">Best next move</h2>
                  </div>
                </div>
                <Button onClick={fetchRecommendation} disabled={recLoading} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="follow-up-recommend-btn">
                  <Sparkles className="w-4 h-4 mr-1"/> {recLoading ? "Thinking…" : (recommendation ? "Refresh" : "Get AI recommendation")}
                </Button>
              </div>
              {recommendation ? (
                <div className="grid md:grid-cols-3 gap-3">
                  <StatBox label="Channel" value={recommendation.recommended_channel} mono />
                  <StatBox label="Urgency" value={recommendation.urgency} mono />
                  <StatBox label="Days since quote" value={recommendation.days_since_quote ?? "—"} mono />
                  <div className="md:col-span-3 bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500 mb-1">Next action</div>
                    <div className="text-zinc-900 font-medium" data-testid="follow-up-next-action">{recommendation.next_action}</div>
                    {recommendation.reasoning && <div className="text-xs text-zinc-600 mt-2">{recommendation.reasoning}</div>}
                  </div>
                  <div className="md:col-span-3 flex flex-wrap gap-2">
                    <Button onClick={()=>draftMessage(recommendation.recommended_channel === "sms" ? "sms" : "email")} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="follow-up-draft-btn">
                      {recommendation.recommended_channel === "sms" ? <MessageSquare className="w-4 h-4 mr-1"/> : <Mail className="w-4 h-4 mr-1"/>}
                      Draft {recommendation.recommended_channel === "sms" ? "SMS" : "email"}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-zinc-500">Get a personalized recommendation using the customer's info, service, previous activity, and time since the quote was sent.</p>
              )}
            </div>
          )}

          {/* Details */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 items-start">
            <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-heading text-xl font-bold">Customer details</h2>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {lead.phone && (
                    <>
                      <a href={`tel:${lead.phone}`} className="text-xs inline-flex items-center gap-1 text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded border border-emerald-200" data-testid="lead-call-btn">
                        <Phone className="w-3 h-3"/> Call
                      </a>
                      <button
                        onClick={() => copyToClipboard(lead.phone, "Phone")}
                        className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded border border-zinc-200"
                        data-testid="lead-copy-phone-btn"
                      >
                        <Copy className="w-3 h-3"/> Copy phone
                      </button>
                    </>
                  )}
                  {lead.email && (
                    <>
                      <a href={`mailto:${lead.email}`} className="text-xs inline-flex items-center gap-1 text-blue-700 hover:bg-blue-50 px-2 py-1 rounded border border-blue-200" data-testid="lead-email-btn">
                        <Mail className="w-3 h-3"/> Email
                      </a>
                      <button
                        onClick={() => copyToClipboard(lead.email, "Email")}
                        className="text-xs inline-flex items-center gap-1 text-zinc-600 hover:bg-zinc-100 px-2 py-1 rounded border border-zinc-200"
                        data-testid="lead-copy-email-btn"
                      >
                        <Copy className="w-3 h-3"/> Copy email
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <Info icon={Phone} label="Phone" value={lead.phone || "—"} />
                <Info icon={Mail} label="Email" value={lead.email || "—"} />
                <Info icon={MapPin} label="Property address" value={lead.property_address || "—"} />
                <Info icon={Calendar} label="Follow-up date" value={lead.follow_up_date ? new Date(lead.follow_up_date).toLocaleDateString() : "—"} />
                <Info icon={DollarSign} label="Estimated value" value={`$${(lead.estimated_value||0).toLocaleString()}`} />
                <Info icon={Zap} label="Source" value={(lead.lead_source||"").replace(/_/g," ")} />
              </div>
              {lead.notes && (
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500 mb-2">Notes</div>
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap">{lead.notes}</div>
                </div>
              )}
            </div>
            {/* Street View — reuses saved lead.latitude/longitude when the
                lead's address was picked via the autocomplete, so no
                re-geocode is needed. Falls back gracefully otherwise. */}
            <StreetViewPanel
              address={lead.property_address || ""}
              coord={
                (lead.latitude != null && lead.longitude != null)
                  ? { lat: lead.latitude, lng: lead.longitude }
                  : null
              }
              title="Street View"
            />
          </div>

          {/* Attribution / source (surfaces inbound-API metadata) */}
          <AttributionPanel lead={lead} onCopy={copyToClipboard} />

          {/* Add note */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <h2 className="font-heading text-xl font-bold mb-3">Log an activity</h2>
            <Textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Called, left voicemail…" rows={3} data-testid="lead-note-input"/>
            <div className="mt-3 flex justify-end">
              <Button onClick={addNote} className="bg-zinc-900 hover:bg-zinc-800 text-white btn-lift" data-testid="lead-add-note-btn">Add note</Button>
            </div>
          </div>
        </div>

        {/* RIGHT — 4 cols */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">Status</h2>
              <span className={`px-2 py-1 text-xs border rounded-md ${s.color}`}>{s.label}</span>
            </div>
            <div className="text-xs text-zinc-500">Created {lead.created_at ? new Date(lead.created_at).toLocaleString() : "—"}</div>
            {lead.lost_reason && (
              <div className="mt-3 text-xs">
                <span className="uppercase font-mono-accent tracking-widest text-zinc-500">Lost reason: </span>
                <span className="text-zinc-800 font-medium capitalize">{lead.lost_reason.replace(/_/g," ")}</span>
              </div>
            )}
            {!automations.sales_active && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-zinc-100 border border-zinc-200" data-testid="automation-safeguard-banner">
                <ShieldOff className="w-4 h-4 text-zinc-600 mt-0.5 shrink-0"/>
                <div className="text-xs text-zinc-700">
                  Sales follow-ups stopped.
                  {lead.status === "completed" && " Post-sale review workflow scheduled."}
                  {lead.status === "lost" && " Reactivation workflow scheduled."}
                </div>
              </div>
            )}
          </div>

          {/* Automation Activity */}
          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="automation-activity-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">Automation activity</h2>
              <span className="font-mono-accent text-xs text-zinc-500">
                <span className="text-emerald-700">{automations.counts.scheduled}</span> · <span className="text-zinc-500">{automations.counts.completed}</span> · <span className="text-rose-600">{automations.counts.canceled}</span>
              </span>
            </div>

            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200" data-testid="scheduler-status-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                <span className="text-[10px] uppercase font-mono-accent tracking-widest text-emerald-700">Scheduler live · 60s</span>
              </div>
              {automations.counts.scheduled > 0 && (
                <Button onClick={dispatchNow} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white btn-lift text-xs h-7" data-testid="dispatch-now-btn">
                  <Zap className="w-3 h-3 mr-1"/> Dispatch now
                </Button>
              )}
            </div>

            {automations.next_scheduled && (
              <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3" data-testid="automation-next-scheduled">
                <div className="text-[10px] uppercase tracking-widest text-[#E04F00] font-mono-accent mb-0.5">Next scheduled</div>
                <div className="text-sm font-medium text-zinc-900">
                  {automations.next_scheduled.channel === "sms" ? "SMS" : "Email"} · {automations.next_scheduled.step} {(KIND_LABELS[automations.next_scheduled.kind]?.text || "follow-up").toLowerCase()}
                </div>
                <div className="text-xs text-zinc-600 font-mono-accent">
                  {new Date(automations.next_scheduled.scheduled_for).toLocaleString()}
                </div>
              </div>
            )}

            {automations.tasks.length === 0 ? (
              <div className="text-sm text-zinc-500">No automations scheduled yet. Move a lead to <span className="font-medium">Quote Sent</span> to schedule the 1/3/7-day follow-up sequence.</div>
            ) : (
              <div className="space-y-2">
                {automations.tasks.map((t) => (
                  <AutomationRow key={t.id} task={t} onCancel={cancelTask} onComplete={completeTask}/>
                ))}
              </div>
            )}
            <div className="mt-3 text-[10px] font-mono-accent text-zinc-400">
              Delivery status for this tenant is shown in Settings → SMS automations.
            </div>
          </div>

          {/* SMS Conversation thread — inbound + outbound + automated in one place */}
          <SmsThread scope="lead" id={id} />

          <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp">
            <h2 className="font-heading text-xl font-bold mb-4">Activity timeline</h2>
            {activities.length === 0 ? (
              <div className="text-sm text-zinc-500">No activity yet.</div>
            ) : (
              <div className="space-y-4">
                {activities.map((a) => (
                  <div key={a.id} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#FF5A00] mt-2 shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-800">{a.content}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        <span className="uppercase font-mono-accent tracking-wider">{a.type}</span>
                        {" · "}
                        {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI draft dialog */}
      <Dialog open={!!draft || drafting} onOpenChange={(o)=>{ if (!o) setDraft(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="ai-draft-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">AI-drafted {draft?.channel || "message"}</DialogTitle>
          </DialogHeader>
          {drafting ? (
            <div className="py-10 text-center text-zinc-500">Gemini is thinking…</div>
          ) : draft ? (
            <div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 whitespace-pre-wrap text-sm font-mono-accent" data-testid="ai-draft-content">
                {draft.draft}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={()=>{ navigator.clipboard.writeText(draft.draft); toast.success("Copied"); }}>
                  <Copy className="w-4 h-4 mr-1"/> Copy
                </Button>
                <Button onClick={()=>setDraft(null)} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white">Done</Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <LeadFormDialog open={editing} onOpenChange={setEditing} onSubmit={saveEdit} initial={lead} campaigns={campaigns}/>

      {/* Lost reason dialog */}
      <Dialog open={lostOpen} onOpenChange={setLostOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Why was this lead lost?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-500">We'll preserve this lead for future reactivation campaigns.</p>
          <div className="mt-2">
            <Select value={lostReason} onValueChange={setLostReason}>
              <SelectTrigger data-testid="lost-reason-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {LOST_REASONS.map((r)=><SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={()=>setLostOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmLost} data-testid="lost-reason-confirm-btn">Mark as lost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KIND_LABELS = {
  quote_follow_up: { text: "Sales", cls: "bg-orange-100 text-[#E04F00] border-orange-200" },
  review_request: { text: "Review", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  reactivation: { text: "Reactivation", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
};

function AutomationRow({ task, onCancel, onComplete }) {
  const [showBody, setShowBody] = useState(false);
  const isDone = task.status === "completed";
  const isCanceled = task.status === "canceled";
  const isPending = task.status === "scheduled";
  const wasAutoDispatched = !!task.dispatched_at;
  const kindMeta = KIND_LABELS[task.kind] || KIND_LABELS.quote_follow_up;
  const Icon = task.channel === "sms" ? MessageSquare : Mail;
  const StateIcon = isDone ? CheckCircle2 : isCanceled ? XCircle : Clock;
  const stateColor = isDone ? "text-emerald-600" : isCanceled ? "text-rose-500" : "text-orange-600";
  const retryText = (task.retry_count || 0) > 0 && isPending ? ` · retry ${task.retry_count}/3` : "";
  return (
    <div className={`rounded-lg border ${isPending ? "border-orange-200 bg-orange-50/40" : "border-zinc-200 bg-zinc-50/60"}`} data-testid={`automation-row-${task.id}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-8 h-8 rounded-md bg-white border border-zinc-200 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-zinc-600"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-900 flex items-center gap-1.5 flex-wrap">
            {task.channel === "sms" ? "SMS" : "Email"} <span className="font-mono-accent text-xs text-zinc-500">· {task.step}</span>
            <span className={`text-[9px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded border ${kindMeta.cls}`}>{kindMeta.text}</span>
            {wasAutoDispatched && <span className="text-[9px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">Auto-sent</span>}
          </div>
          <div className={`text-xs font-mono-accent flex items-center gap-1 ${stateColor}`}>
            <StateIcon className="w-3 h-3"/>
            {isDone && (wasAutoDispatched
              ? `Sent ${task.dispatched_at ? new Date(task.dispatched_at).toLocaleString() : ""}`
              : `Completed ${task.completed_at ? new Date(task.completed_at).toLocaleDateString() : ""}`)}
            {isCanceled && `Canceled${task.cancel_reason ? " · " + task.cancel_reason.replace(/_/g," ") : ""}`}
            {isPending && `Scheduled ${new Date(task.scheduled_for).toLocaleString()}${retryText}`}
          </div>
        </div>
        {isPending && (
          <div className="flex gap-1">
            <button onClick={()=>onComplete(task.id)} className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid={`automation-complete-${task.id}`}>Done</button>
            <button onClick={()=>onCancel(task.id)} className="text-xs px-2 py-1 rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50" data-testid={`automation-cancel-${task.id}`}>Cancel</button>
          </div>
        )}
        {wasAutoDispatched && task.dispatched_body && (
          <button onClick={()=>setShowBody((v)=>!v)} className="text-xs text-zinc-500 hover:text-zinc-900 font-mono-accent" data-testid={`automation-view-body-${task.id}`}>
            {showBody ? "Hide" : "View"}
          </button>
        )}
      </div>
      {wasAutoDispatched && showBody && task.dispatched_body && (
        <div className="border-t border-zinc-200 px-3 py-2 bg-white text-xs">
          {task.dispatched_subject && (
            <div className="mb-1"><span className="uppercase font-mono-accent text-[10px] text-zinc-500 tracking-widest">Subject: </span><span className="text-zinc-800">{task.dispatched_subject}</span></div>
          )}
          <div className="whitespace-pre-wrap text-zinc-700">{task.dispatched_body}</div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, mono }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3">
      <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      <div className={`text-lg font-bold ${mono ? "font-mono-accent" : ""} text-zinc-900 capitalize`}>{value}</div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-zinc-600"/>
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
        <div className="text-zinc-900 font-medium capitalize truncate">{value}</div>
      </div>
    </div>
  );
}

function AttributionPanel({ lead, onCopy }) {
  const utm = lead.utm || {};
  const hasAttribution =
    !!lead.external_id || !!lead.referring_url || !!lead.api_key_prefix ||
    !!lead.gclid || !!lead.fbclid ||
    !!(utm.source || utm.medium || utm.campaign || utm.content || utm.term) ||
    !!lead.rv_details;
  if (!hasAttribution) return null;

  const Row = ({ icon: Icon, label, value, mono = false, copyable = false, link = false, testid }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3" data-testid={testid}>
        <div className="w-7 h-7 rounded-md bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5">
          <Icon className="w-3.5 h-3.5 text-zinc-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
          <div className={`text-sm text-zinc-900 break-all ${mono ? "font-mono-accent" : ""}`}>
            {link ? (
              <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">{value}</a>
            ) : value}
          </div>
        </div>
        {copyable && (
          <button
            onClick={() => onCopy?.(value, label)}
            className="text-[10px] inline-flex items-center gap-1 text-zinc-500 hover:bg-zinc-100 px-2 py-1 rounded"
            data-testid={`${testid}-copy`}
          >
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="lead-attribution-panel">
      <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2">
        <Fingerprint className="w-5 h-5 text-[#FF5A00]" /> Attribution &amp; source
      </h2>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <Row icon={Tag} label="External ID" value={lead.external_id} mono copyable testid="attr-external-id" />
        <Row icon={KeyRound} label="Inbound API key" value={lead.api_key_prefix ? `${lead.api_key_prefix}…` : null} mono testid="attr-api-key-prefix" />
        <Row icon={Globe} label="Referring URL" value={lead.referring_url} link testid="attr-referring-url" />
        <Row icon={Link2} label="UTM source" value={utm.source} mono testid="attr-utm-source" />
        <Row icon={Link2} label="UTM medium" value={utm.medium} mono testid="attr-utm-medium" />
        <Row icon={Link2} label="UTM campaign" value={utm.campaign} mono testid="attr-utm-campaign" />
        <Row icon={Link2} label="UTM content" value={utm.content} mono testid="attr-utm-content" />
        <Row icon={Link2} label="UTM term" value={utm.term} mono testid="attr-utm-term" />
        <Row icon={Tag} label="gclid" value={lead.gclid} mono copyable testid="attr-gclid" />
        <Row icon={Tag} label="fbclid" value={lead.fbclid} mono copyable testid="attr-fbclid" />
      </div>
      {lead.rv_details && Object.keys(lead.rv_details || {}).length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="attr-rv-details">
          <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 mb-2">RV details</div>
          <pre className="text-xs bg-zinc-50 border border-zinc-100 rounded p-3 whitespace-pre-wrap break-all">
{JSON.stringify(lead.rv_details, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
